import { useEffect, useState } from 'react'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import type { SheetLayout } from '@/components/shared/sheet-layout'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { UpdateBookingQuestionnaireRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import type { Questionnaire, QuestionnaireItem } from '@/gen/cortado/v1/questionnaires_pb'
import {
  cloneDraftItemsWithNewIds,
  draftFromQuestionnaire,
  itemHasAnswer,
  questionnaireItemsFromDraft,
  sortedQuestionnaireItems,
  validateQuestionnaireDraft,
  type BookingQuestionnaireDraftItem,
} from '@/lib/booking-questionnaire'
import { BookingQuestionnaireDesigner } from '@/components/settings/organization/booking-questionnaire-designer'
import { useCurrentOrganization } from '@/stores/organization.store'
import { useOrganizationEventTypes } from '@/stores/event-types.store'
import { useUpdateBookingQuestionnaire } from '@/stores/bookings.store'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'

interface BookingQuestionnaireEditorSheetProps {
  organizationId: string
  bookingId: string
  eventTypeKey?: string
  questionnaire?: Questionnaire
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 'pipeline' applies the shared pipeline sheet width and footer layout. */
  layout?: SheetLayout
}

function findRemovedAnsweredItems(
  original: QuestionnaireItem[],
  draftIds: Set<string>,
): QuestionnaireItem[] {
  return original.filter((item) => itemHasAnswer(item) && !draftIds.has(item.id))
}

export function BookingQuestionnaireEditorSheet({
  organizationId,
  bookingId,
  eventTypeKey,
  questionnaire,
  open,
  onOpenChange,
  layout,
}: BookingQuestionnaireEditorSheetProps) {
  const [items, setItems] = useState<BookingQuestionnaireDraftItem[]>([])
  const [pendingRemovedAnswered, setPendingRemovedAnswered] = useState<QuestionnaireItem[] | null>(null)

  const { currentOrganization } = useCurrentOrganization()
  const { eventTypes } = useOrganizationEventTypes(organizationId)
  const { mutation: updateMutation, updateBookingQuestionnaire } = useUpdateBookingQuestionnaire()

  const isEditMode = sortedQuestionnaireItems(questionnaire).length > 0

  useEffect(() => {
    if (open) {
      setItems(draftFromQuestionnaire(questionnaire))
      setPendingRemovedAnswered(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, questionnaire])

  const matchingEventType = eventTypeKey
    ? eventTypes.find((candidate) => candidate.key === eventTypeKey)
    : undefined

  const defaultTemplateItems = sortedQuestionnaireItems(currentOrganization?.defaultQuestionnaire)
  const eventTypeTemplateItems = sortedQuestionnaireItems(matchingEventType?.questionnaire)
  const hasTemplateItems = defaultTemplateItems.length > 0 || eventTypeTemplateItems.length > 0

  function handleCopyFromTemplates() {
    const copiedItems = cloneDraftItemsWithNewIds([
      ...draftFromQuestionnaire(currentOrganization?.defaultQuestionnaire),
      ...draftFromQuestionnaire(matchingEventType?.questionnaire),
    ])
    setItems((current) => [...current, ...copiedItems])
  }

  async function performSave() {
    try {
      await updateBookingQuestionnaire(create(UpdateBookingQuestionnaireRequestSchema, {
        bookingId,
        items: questionnaireItemsFromDraft(items),
      }))
      setPendingRemovedAnswered(null)
      onOpenChange(false)
      toast.success('Questionnaire saved')
    } catch (error) {
      reportError(error, { feature: 'booking-questionnaire-editor' })
      toast.error('Failed to save questionnaire', {
        description: getErrorDescription(error),
      })
    }
  }

  function handleSave() {
    const validationError = validateQuestionnaireDraft(items)
    if (validationError) {
      toast.error(validationError)
      return
    }

    const draftIds = new Set(items.map((item) => item.id))
    const removedAnswered = findRemovedAnsweredItems(
      sortedQuestionnaireItems(questionnaire),
      draftIds,
    )

    if (removedAnswered.length > 0) {
      setPendingRemovedAnswered(removedAnswered)
      return
    }

    void performSave()
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className={cn(layout === 'pipeline' ? 'w-full sm:max-w-xl' : 'sm:max-w-[640px]')}>
          <SheetHeader>
            <SheetTitle>{isEditMode ? 'Edit questionnaire' : 'Add questionnaire'}</SheetTitle>
            <SheetDescription>
              These questions apply to this booking only. Editing them here does not
              change any organization or event-type template.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                disabled={!hasTemplateItems}
                onClick={handleCopyFromTemplates}
              >
                Copy from templates
              </Button>
              <BookingQuestionnaireDesigner
                items={items}
                onChange={setItems}
                label="Booking questions"
                description="Questions collected for this booking. Copying from templates is a one-time seed — it does not stay in sync with them."
              />
            </div>
          </SheetBody>
          <SheetFooter className={cn(layout === 'pipeline' && 'sm:justify-between')}>
            <SheetClose asChild>
              <Button type="button" variant="outline" disabled={updateMutation.isPending}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="button" onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={!!pendingRemovedAnswered}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingRemovedAnswered(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove answered questions?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>These questions have answers that will be lost:</p>
                <ul className="list-disc pl-5">
                  {pendingRemovedAnswered?.map((item) => (
                    <li key={item.id}>{item.prompt}</li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={updateMutation.isPending}
              onClick={(clickEvent) => {
                clickEvent.preventDefault()
                void performSave()
              }}
            >
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove and save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
